import React, { useState, useRef, useEffect, useId } from 'react';

function CustomSelect({ options, defaultValue, className = '', onChange, size = 'sm' }) {
  const isObject = (o) => o != null && typeof o === 'object' && 'label' in o;
  const getLabel = (o) => isObject(o) ? o.label : o;
  const getValue = (o) => isObject(o) ? o.value : o;

  const initial = options.find(o => getValue(o) === defaultValue) || options[0];
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initial);
  const ref = useRef(null);
  const instanceId = useId();
  const xs = size === 'xs';

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close this dropdown when another CustomSelect opens
  useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail !== instanceId) setIsOpen(false);
    };
    document.addEventListener('customselect-open', handleOtherOpen);
    return () => document.removeEventListener('customselect-open', handleOtherOpen);
  }, [instanceId]);

  const handleToggle = () => {
    const opening = !isOpen;
    if (opening) {
      document.dispatchEvent(new CustomEvent('customselect-open', { detail: instanceId }));
    }
    setIsOpen(opening);
  };

  return (
    <div ref={ref} className={`relative ${isOpen ? 'z-50' : ''} ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full border-2 border-gray-300 rounded-lg ${xs ? 'px-2 py-1' : 'p-3'} text-left flex items-center justify-between focus:border-blue-500 focus:outline-none bg-white ${xs ? 'text-xs' : 'text-sm'} text-gray-900`}
      >
        <span className="whitespace-nowrap truncate">{getLabel(selected)}</span>
        <svg width={xs ? 12 : 16} height={xs ? 12 : 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-gray-400 flex-shrink-0 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
          {options.map(option => (
            <button
              key={getValue(option)}
              type="button"
              onClick={() => { setSelected(option); setIsOpen(false); if (onChange) onChange(getValue(option)); }}
              className={`w-full text-left px-3 ${xs ? 'py-2 text-xs' : 'py-2.5 text-sm'} hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 ${getValue(option) === getValue(selected) ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-900'}`}
            >
              {getLabel(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
